import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import api from '../api';

/**
 * Notifications inbox. Phase 4.
 * Groups by day, marks all read on visit, deep-links each item to the right place.
 */

const RELATIVE = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dd = Math.floor(h / 24);
  return dd < 7 ? `${dd}d ago` : d.toLocaleDateString();
};

const BADGE = {
  royalty: { color: '#FF5722', bg: 'rgba(255, 87, 34,0.16)', emoji: '👑' },
  referral: { color: '#00B4FF', bg: 'rgba(0, 180, 255,0.14)', emoji: '🤝' },
  trade: { color: '#B4FF00', bg: 'rgba(180, 255, 0,0.14)', emoji: '📈' },
  post: { color: '#00B4FF', bg: 'rgba(0, 180, 255,0.14)', emoji: '📝' },
  live: { color: '#FF5722', bg: 'rgba(255, 87, 34,0.14)', emoji: '🔴' },
  system: { color: '#655E7D', bg: 'rgba(151,145,172,0.14)', emoji: '⚡' },
};

export default function Notifications() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get('/notifications').then(({ data }) => setItems(data.notifications || [])).catch(() => setItems([]));
    // Mark all as read as soon as user opens the inbox
    api.post('/notifications/mark-read').catch(() => {});
  }, []);

  const openItem = (n) => {
    const kind = n.kind || '';

    // Perks — fan-side notifications go to the perks shelf inside the Clan
    if ((kind === 'perk_added' || kind === 'perk_fulfilled' || kind === 'perk_cancelled') && n.clan_handle) {
      return navigate(`/clan/${n.clan_handle}/feed?tab=perks`, { state: { from: '/notifications' } });
    }
    // Perk redemption — creator gets pinged; take them to Manage Clan so they can fulfil
    if (kind === 'perk_redemption') {
      return navigate('/manage-clan');
    }

    // Badges — show the user their own Creator Profile where the badge shelf lives
    if (kind === 'badge') {
      if (user?.handle) return navigate(`/creator/${user.handle}`);
      return navigate('/profile');
    }

    // Airdrops — fans see the credit, creators manage the drop
    if (kind === 'airdrop_received') return navigate('/wallet');
    if (kind === 'airdrop_fired' || kind === 'airdrop_failed_funds') return navigate('/manage-clan');

    // Milestones — creator-side, on Manage Clan
    if (kind === 'milestone') return navigate('/manage-clan');

    // Waitlist fulfilled — take them to the Clan they were waiting on
    if (kind === 'waitlist_fulfilled' && n.clan_handle) {
      return navigate(`/creator/${n.clan_handle}`);
    }

    // Creator gift — points credited, wallet shows the receipt
    if (kind === 'creator_gift') return navigate('/wallet');

    // DMs — open the conversation with the sender
    if (kind === 'dm') {
      if (n.actor_handle) return navigate(`/messages/${n.actor_handle}`);
      return navigate('/messages');
    }

    // Bounties — fans see their win in wallet ledger; creators review from Manage Clan
    if (kind === 'bounty_won') return navigate('/wallet');
    if (kind === 'bounty_submission') return navigate('/manage-clan');

    // Existing kinds
    if (kind === 'post' && n.clan_handle) return navigate(`/clan/${n.clan_handle}/feed`, { state: { from: '/notifications' } });
    if (kind === 'live' && n.ref_id) return navigate(`/launch/${n.ref_id}`);
    if (kind === 'royalty' && n.actor_handle) return navigate(`/creator/${n.actor_handle}`);
    if (kind === 'referral' && n.clan_handle) return navigate(`/creator/${n.clan_handle}`);

    // System — surface the related Clan if we have one, otherwise home
    if (kind === 'system' && n.clan_handle) return navigate(`/clan/${n.clan_handle}/feed`, { state: { from: '/notifications' } });

    // Fallback — home is friendlier than a wallet dead-end
    navigate('/home');
  };

  return (
    <AppShell activeTab="home">
      <div className="notif-wrap" data-testid="notifications-page">
        <div className="notif-top">
          <h1 className="notif-title">Notifications</h1>
        </div>

        {items === null && <div className="notif-loading" data-testid="notif-loading">loading…</div>}

        {items && items.length === 0 && (
          <div className="notif-empty" data-testid="notif-empty">
            <div className="notif-empty-emoji">📭</div>
            <div className="notif-empty-title">You're all caught up</div>
            <p className="notif-empty-sub">Trades, royalties, and Clan updates will land here.</p>
          </div>
        )}

        {items && items.length > 0 && (
          <div className="notif-list" data-testid="notif-list">
            {items.map((n) => {
              const b = BADGE[n.icon_hint] || BADGE.system;
              return (
                <div
                  className={`notif-row${n.read ? '' : ' unread'}`}
                  key={n.id}
                  onClick={() => openItem(n)}
                  role="button"
                  tabIndex={0}
                  data-testid={`notif-${n.id}`}
                >
                  <div className="notif-badge" style={{ color: b.color, background: b.bg }}>
                    <span>{b.emoji}</span>
                  </div>
                  <div className="notif-body">
                    <div className="notif-head">{n.title}</div>
                    {n.subtitle && <div className="notif-sub">{n.subtitle}</div>}
                    <div className="notif-time">{RELATIVE(n.created_at)}</div>
                  </div>
                  {!n.read && <div className="notif-unread-dot" />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
